import { HttpTypes } from "@medusajs/types"

type StoreProductCategory = HttpTypes.StoreProductCategory

type MatchRule =
  | { type: "exact"; name: string }
  | { type: "partial"; name: string }

interface LeafGroupConfig {
  id: string
  name: string
  defaultExpanded?: boolean
  rules: MatchRule[]
}

interface ParentGroupConfig {
  id: string
  name: string
  defaultExpanded?: boolean
  subgroups: LeafGroupConfig[]
}

type GroupConfig = LeafGroupConfig | ParentGroupConfig

function isParentGroup(g: GroupConfig): g is ParentGroupConfig {
  return "subgroups" in g
}

function matchesRule(catName: string, rule: MatchRule): boolean {
  const normalized = catName.toLowerCase()
  const target = rule.name.toLowerCase()
  return rule.type === "exact" ? normalized === target : normalized.includes(target)
}

function matchesAnyRule(catName: string, rules: MatchRule[]): boolean {
  return rules.some((rule) => matchesRule(catName, rule))
}

const CHOKYO_GROUPS: GroupConfig[] = [
  {
    id: "virtual-cat",
    name: "Cat",
    defaultExpanded: true,
    subgroups: [
      {
        id: "virtual-cat-food",
        name: "Food",
        rules: [
          { type: "exact", name: "CAT WET FOOD" },
          { type: "exact", name: "CAT Treats" },
          { type: "exact", name: "Wet Food" },
          { type: "exact", name: "Dry Food" },
          { type: "exact", name: "Inaba Ciao" },
          { type: "exact", name: "CATZ FINEFOOD" },
          { type: "exact", name: "Feline Natural" },
          { type: "exact", name: "Royal Canin" },
          { type: "exact", name: "Freeze Dried Treats" },
          { type: "exact", name: "FD TREATS" },
          { type: "exact", name: "Zealandia" },
          { type: "exact", name: "GranataPet" },
        ],
      },
      {
        id: "virtual-cat-furniture",
        name: "Furniture & Scratchers",
        rules: [
          { type: "exact", name: "Cat Furniture" },
          { type: "exact", name: "Cat Tree" },
          { type: "exact", name: "NOCATTREE" },
          { type: "partial", name: "Cat Scratchers" },
          { type: "exact", name: "Floor Standing Cat Tree" },
          { type: "exact", name: "Cactus Collection" },
        ],
      },
      {
        id: "virtual-cat-litter",
        name: "Litter & Toilet",
        rules: [
          { type: "exact", name: "Cat Litter" },
          { type: "exact", name: "Cat Litter Box" },
          { type: "exact", name: "Cat Litter Boxes & Accessories" },
          { type: "exact", name: "Litter & Accessories" },
        ],
      },
      {
        id: "virtual-cat-toys",
        name: "Toys",
        rules: [{ type: "partial", name: "Cat Teasers" }],
      },
    ],
  },
  {
    id: "virtual-dog",
    name: "Dog",
    defaultExpanded: true,
    subgroups: [
      {
        id: "virtual-dog-food",
        name: "Food",
        rules: [
          { type: "exact", name: "DOG WET FOOD" },
          { type: "exact", name: "DOG Treats" },
          { type: "exact", name: "Dog Dry Food" },
          { type: "exact", name: "Dental Chews" },
          { type: "exact", name: "K9 NATURAL" },
          { type: "exact", name: "Taste of the Wild" },
          { type: "exact", name: "Taste of the Wild Wet Food" },
          { type: "exact", name: "DOGZ FINEFOOD" },
          { type: "exact", name: "Fresh Dog Food" },
        ],
      },
      {
        id: "virtual-dog-toilet",
        name: "Toilet",
        rules: [
          { type: "exact", name: "Indoor Dog Toilets" },
          { type: "exact", name: "Pee & Toilet" },
          { type: "exact", name: "🐕Indoor Dog Toilets" },
        ],
      },
    ],
  },
  {
    id: "virtual-small-animals",
    name: "Small Animals",
    defaultExpanded: true,
    rules: [
      { type: "exact", name: "Small Animals" },
      { type: "partial", name: "Small Animals Food" },
      { type: "partial", name: "Small Animals Bedding" },
      { type: "partial", name: "Small Animal Toys" },
    ],
  },
  {
    id: "virtual-accessories",
    name: "Accessories & Living",
    defaultExpanded: true,
    rules: [
      { type: "exact", name: "Bedding" },
      { type: "exact", name: "bedding & comfort" },
      { type: "exact", name: "Feeding" },
      { type: "partial", name: "Slow Feeder Bowls" },
      { type: "exact", name: "Carrier" },
      { type: "exact", name: "Trolleys / Strollers" },
      { type: "exact", name: "Leash & Clothing" },
      { type: "partial", name: "Bath & Cleaning" },
      { type: "exact", name: "Cleaning & Deodorizer" },
      { type: "exact", name: "Brush & Deshedding" },
      { type: "exact", name: "Treadmill" },
      { type: "partial", name: "Interactive Puzzle Toy" },
    ],
  },
  {
    id: "virtual-brands",
    name: "Brands",
    defaultExpanded: false,
    rules: [
      { type: "exact", name: "BRIDGE.DOG" },
      { type: "exact", name: "BRIDGE.DOG Basic" },
      { type: "exact", name: "BRIDGE.DOG Limited Edition" },
      { type: "exact", name: "KOJIMA" },
      { type: "exact", name: "PETIO" },
      { type: "exact", name: "Rosewood" },
      { type: "exact", name: "Pakeway" },
      { type: "exact", name: "FREEZY PAWS" },
      { type: "exact", name: "PawDry" },
      { type: "exact", name: "Purlab" },
      { type: "exact", name: "Honeypot Cat" },
      { type: "exact", name: "Purr Haven" },
      { type: "exact", name: "Pawkith" },
      { type: "exact", name: "pidan" },
      { type: "exact", name: "VETRESKA" },
      { type: "exact", name: "Petkit" },
      { type: "exact", name: "Catit" },
      { type: "exact", name: "Catidea" },
      { type: "exact", name: "F10" },
      { type: "exact", name: "Iris" },
      { type: "exact", name: "Unicharm" },
      { type: "exact", name: "NAS" },
      { type: "exact", name: "NexGard Spectra" },
      { type: "exact", name: "Orijen" },
      { type: "exact", name: "Himalayan" },
      { type: "exact", name: "maybopet" },
      { type: "exact", name: "PETSHY" },
      { type: "exact", name: "MissPet" },
      { type: "exact", name: "purroom" },
      { type: "exact", name: "OHF" },
      { type: "exact", name: "SUPWOW" },
      { type: "exact", name: "Okey Doggy" },
      { type: "exact", name: "Freecat" },
      { type: "exact", name: "Little Cheeks" },
      { type: "exact", name: "Pink Wish" },
      { type: "exact", name: "prime100" },
      { type: "exact", name: "Nature Island" },
      { type: "exact", name: "RANOVA" },
      { type: "exact", name: "Natural Core" },
      { type: "exact", name: "GranataPet" },
      { type: "partial", name: "Ziwi Peak" },
      { type: "partial", name: "K9 Natural & Feline" },
      { type: "exact", name: "Hell's Kitchen" },
      { type: "exact", name: "YES I'M REAL" },
    ],
  },
  {
    id: "virtual-deals",
    name: "Deals & Collections",
    defaultExpanded: true,
    rules: [
      { type: "exact", name: "Hot Sale" },
      { type: "exact", name: "Top Sale" },
      { type: "exact", name: "New arrivals" },
      { type: "exact", name: "All" },
      { type: "exact", name: "Barbie Month Specials" },
      { type: "exact", name: "Christmas sale" },
      { type: "exact", name: "Sales" },
      { type: "exact", name: "PRE-ORDER SALE" },
      { type: "exact", name: "VALUE PACK" },
      { type: "exact", name: "Get a Free Automatic Litter Box" },
      { type: "exact", name: "top 100" },
      { type: "exact", name: "Customize" },
      { type: "exact", name: "Gift card" },
    ],
  },
]

function makeVirtual(
  id: string,
  name: string,
  children: StoreProductCategory[],
  defaultExpanded: boolean
): StoreProductCategory {
  const handle = id.replace(/^virtual-/, "")
  return {
    id,
    name,
    handle,
    description: "",
    rank: null,
    parent_category_id: null,
    parent_category: null,
    category_children: children,
    created_at: "",
    updated_at: "",
    deleted_at: null,
    metadata: { defaultExpanded },
  } as unknown as StoreProductCategory
}

/**
 * Builds a category tree from a flat list of categories.
 *
 * - If any root category already has children, returns the input unchanged.
 * - For 'chokyo', groups flat categories into virtual parent categories.
 * - Unmatched categories are placed in an 'Other' group.
 */
export function buildCategoryTree(
  categories: StoreProductCategory[],
  sourceStore: string
): StoreProductCategory[] {
  const rootCats = categories.filter((c) => !c.parent_category)

  // Already a tree — return as-is
  if (rootCats.some((c) => (c.category_children || []).length > 0)) {
    return categories
  }

  if (sourceStore !== "chokyo") {
    return categories
  }

  const assigned = new Set<string>()
  const result: StoreProductCategory[] = []

  for (const group of CHOKYO_GROUPS) {
    if (isParentGroup(group)) {
      const subgroupVirtuals: StoreProductCategory[] = []

      for (const sub of group.subgroups) {
        const matched = categories.filter(
          (cat) => !assigned.has(cat.id) && matchesAnyRule(cat.name, sub.rules)
        )
        matched.forEach((cat) => assigned.add(cat.id))

        if (matched.length > 0) {
          subgroupVirtuals.push(makeVirtual(sub.id, sub.name, matched, true))
        }
      }

      if (subgroupVirtuals.length > 0) {
        result.push(
          makeVirtual(group.id, group.name, subgroupVirtuals, group.defaultExpanded ?? true)
        )
      }
    } else {
      const matched = categories.filter(
        (cat) => !assigned.has(cat.id) && matchesAnyRule(cat.name, group.rules)
      )
      matched.forEach((cat) => assigned.add(cat.id))

      if (matched.length > 0) {
        result.push(makeVirtual(group.id, group.name, matched, group.defaultExpanded ?? true))
      }
    }
  }

  // Unmatched categories go into Other
  const unmatched = categories.filter((cat) => !assigned.has(cat.id))
  if (unmatched.length > 0) {
    result.push(makeVirtual("virtual-other", "Other", unmatched, true))
  }

  return result
}
